import React, { useMemo, useState } from 'react'
import styled from 'styled-components'
import moment from 'moment'
import { taskStatusMeta } from 'features/taskManagement/taskStatusConstants'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import EditIcon from '@mui/icons-material/Edit'
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline'
import PersonAddIcon from '@mui/icons-material/PersonAdd'
import PersonRemoveIcon from '@mui/icons-material/PersonRemove'
import SaveIcon from '@mui/icons-material/Save'
import SendIcon from '@mui/icons-material/Send'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CancelIcon from '@mui/icons-material/Cancel'
import SwapHorizIcon from '@mui/icons-material/SwapHoriz'
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined'
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh'
import EmailIcon from '@mui/icons-material/Email'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import { maskEmail } from 'utils/string'

const GROUP_MIN_COUNT = 2
const NON_GROUPABLE_ACTIONS = ['commented', 'comment', 'note_added']

const actionConfig = {
  created: {
    icon: AddCircleOutlineIcon,
    color: '#1890ff',
    bg: '#e6f7ff',
    label: 'Task Created'
  },
  updated: {
    icon: EditIcon,
    color: '#722ed1',
    bg: '#f9f0ff',
    label: 'Task Updated'
  },
  approval_changed: {
    icon: FactCheckOutlinedIcon,
    color: '#7c3aed',
    bg: '#ede9fe',
    label: 'Approval Changed'
  },
  renamed: {
    icon: DriveFileRenameOutlineIcon,
    color: '#0891b2',
    bg: '#ecfeff',
    label: 'Task Renamed'
  },
  assigned: {
    icon: PersonAddIcon,
    color: '#52c41a',
    bg: '#f6ffed',
    label: 'Assigned'
  },
  unassigned: {
    icon: PersonRemoveIcon,
    color: '#fa8c16',
    bg: '#fff7e6',
    label: 'Unassigned'
  },
  external_assigned: {
    icon: EmailIcon,
    color: '#13c2c2',
    bg: '#e6fffb',
    label: 'External User Assigned'
  },
  external_unassigned: {
    icon: PersonRemoveIcon,
    color: '#fa8c16',
    bg: '#fff7e6',
    label: 'External User Removed'
  },
  form_saved: {
    icon: SaveIcon,
    color: '#595959',
    bg: '#f5f5f5',
    label: 'Form Saved'
  },
  submitted: {
    icon: SendIcon,
    color: '#722ed1',
    bg: '#f9f0ff',
    label: 'Submitted for Approval'
  },
  approved: {
    icon: CheckCircleIcon,
    color: '#389e0d',
    bg: '#d9f7be',
    label: 'Approved'
  },
  rejected: {
    icon: CancelIcon,
    color: '#f5222d',
    bg: '#fff1f0',
    label: 'Rejected'
  },
  status_changed: {
    icon: SwapHorizIcon,
    color: '#1890ff',
    bg: '#e6f7ff',
    label: 'Status Changed'
  },
  signed_off: {
    icon: CheckCircleIcon,
    color: '#059669',
    bg: '#ecfdf5',
    label: 'Signed Off'
  },
  automation_created: {
    icon: AutoFixHighIcon,
    color: '#d97706',
    bg: '#fef3c7',
    label: 'Automation Task Created'
  },
  document_attached: {
    icon: DescriptionOutlinedIcon,
    color: '#2aa6b3',
    bg: '#e6f7f9',
    label: 'Document Linked'
  }
}

function buildGroupedTimeline(activityLogs) {
  const result = []
  let cursor = 0

  while (cursor < activityLogs.length) {
    const log = activityLogs[cursor]

    if (NON_GROUPABLE_ACTIONS.includes(log.action)) {
      result.push({ type: 'log', log })
      cursor++
      continue
    }

    const run = [log]
    let probe = cursor + 1
    while (probe < activityLogs.length) {
      const next = activityLogs[probe]
      if (next.action !== log.action) break
      if (next.actorName !== log.actorName) break
      run.push(next)
      probe++
    }

    if (run.length >= GROUP_MIN_COUNT) {
      const sortedByTime = [...run].sort((a, b) =>
        moment(a.createdAt).diff(moment(b.createdAt)),
      )
      result.push({
        type: 'group',
        action: log.action,
        actorName: log.actorName,
        count: run.length,
        firstAt: sortedByTime[0].createdAt,
        lastAt: sortedByTime[sortedByTime.length - 1].createdAt,
        logs: run,
      })
      cursor = probe
    } else {
      result.push({ type: 'log', log })
      cursor++
    }
  }

  return result
}

const TaskActivityTimeline = ({ activityLogs = [] }) => {
  const items = useMemo(() => buildGroupedTimeline(activityLogs), [activityLogs])
  const [expandedGroups, setExpandedGroups] = useState(() => new Set())

  if (!activityLogs || activityLogs.length === 0) {
    return (
      <EmptyState>
        No activity recorded yet.
      </EmptyState>
    )
  }

  const toggleGroup = (key) => {
    setExpandedGroups(prev => {
      const next = new Set(prev)
      next.has(key) ? next.delete(key) : next.add(key)
      return next
    })
  }

  const renderLog = (log, isLast) => {
    const config = actionConfig[log.action] || actionConfig.updated
    const IconComponent = config.icon
    return (
      <TimelineItem key={log.id} $isLast={isLast}>
        <TimelineConnector $isLast={isLast} $color={config.color} />
        <TimelineIconWrapper $color={config.color} $bg={config.bg}>
          <IconComponent />
        </TimelineIconWrapper>
        <TimelineContent>
          <TimelineHeader>
            <TimelineAction $color={config.color}>
              {config.label}
            </TimelineAction>
            <TimelineDate>
              {moment(log.createdAt).format('DD MMM YYYY, HH:mm')}
            </TimelineDate>
          </TimelineHeader>
          {log.description && (
            <TimelineDescription>{log.description}</TimelineDescription>
          )}
          {log.actorName && (
            <TimelineActor>
              by <strong>{log.actorName.includes('@') ? maskEmail(log.actorName) : log.actorName}</strong>
            </TimelineActor>
          )}
          {log.previousStatus && log.newStatus && (
            <StatusChange>
              <StatusBadge $status={log.previousStatus}>
                {formatStatus(log.previousStatus)}
              </StatusBadge>
              <Arrow>→</Arrow>
              <StatusBadge $status={log.newStatus}>
                {formatStatus(log.newStatus)}
              </StatusBadge>
            </StatusChange>
          )}
          {log.metadata?.externalEmail && (
            <ExternalEmailTag>
              <EmailIcon style={{ fontSize: 14 }} />
              {log.metadata.externalName && <span>{log.metadata.externalName}: </span>}
              {maskEmail(log.metadata.externalEmail)}
            </ExternalEmailTag>
          )}
          {log.metadata?.previousTitle && log.metadata?.newTitle && (
            <TitleChange>
              <TitleValue $type="old">{log.metadata.previousTitle}</TitleValue>
              <Arrow>→</Arrow>
              <TitleValue $type="new">{log.metadata.newTitle}</TitleValue>
            </TitleChange>
          )}
          {log.metadata?.rejectionReason && (
            <RejectionReason>
              Reason: "{log.metadata.rejectionReason}"
            </RejectionReason>
          )}
        </TimelineContent>
      </TimelineItem>
    )
  }

  return (
    <TimelineContainer>
      {items.map((item, index) => {
        const isLast = index === items.length - 1

        if (item.type === 'log') {
          return renderLog(item.log, isLast)
        }

        const config = actionConfig[item.action] || actionConfig.updated
        const IconComponent = config.icon
        const groupKey = `group-${item.action}-${item.firstAt}-${item.lastAt}-${item.actorName}`
        const isExpanded = expandedGroups.has(groupKey)
        const fromLabel = moment(item.firstAt).format('DD MMM, HH:mm')
        const toLabel = moment(item.lastAt).format('DD MMM, HH:mm')
        const sameDay = moment(item.firstAt).isSame(item.lastAt, 'day')
        const rangeLabel = sameDay
          ? `${moment(item.firstAt).format('DD MMM YYYY')} · ${moment(item.firstAt).format('HH:mm')} – ${moment(item.lastAt).format('HH:mm')}`
          : `${fromLabel} – ${toLabel}`

        return (
          <TimelineItem key={groupKey} $isLast={isLast}>
            <TimelineConnector $isLast={isLast} $color={config.color} />
            <TimelineIconWrapper $color={config.color} $bg={config.bg}>
              <IconComponent />
            </TimelineIconWrapper>
            <TimelineContent>
              <TimelineHeader>
                <TimelineAction $color={config.color}>
                  {config.label} {item.count}×
                </TimelineAction>
                <TimelineDate>{rangeLabel}</TimelineDate>
              </TimelineHeader>
              {item.actorName && (
                <TimelineActor>
                  by <strong>{item.actorName.includes('@') ? maskEmail(item.actorName) : item.actorName}</strong>
                </TimelineActor>
              )}
              <GroupToggle onClick={() => toggleGroup(groupKey)}>
                {isExpanded ? <ExpandLessIcon style={{ fontSize: 16 }} /> : <ExpandMoreIcon style={{ fontSize: 16 }} />}
                {isExpanded ? 'Hide individual entries' : `Show all ${item.count}`}
              </GroupToggle>
              {isExpanded && (
                <NestedTimeline>
                  {item.logs.map(log => (
                    <NestedRow key={log.id}>
                      <NestedDot />
                      <NestedTime>{moment(log.createdAt).format('DD MMM YYYY, HH:mm:ss')}</NestedTime>
                      {log.description && (
                        <NestedDescription>{log.description}</NestedDescription>
                      )}
                    </NestedRow>
                  ))}
                </NestedTimeline>
              )}
            </TimelineContent>
          </TimelineItem>
        )
      })}
    </TimelineContainer>
  )
}

const formatStatus = (status) => {
  const statusLabels = {
    unassigned: 'Unassigned',
    assigned: 'Assigned',
    pending_dependent: 'Pending',
    in_progress: 'In Progress',
    on_hold: 'On Hold',
    awaiting_approval: 'Pending Approval',
    completed: 'Completed'
  }
  return statusLabels[status] || status
}

export default TaskActivityTimeline

const TimelineContainer = styled.div`
  position: relative;
`

const TimelineItem = styled.div`
  display: flex;
  position: relative;
  padding-bottom: ${({ $isLast }) => $isLast ? '0' : '24px'};
`

const TimelineConnector = styled.div`
  position: absolute;
  left: 15px;
  top: 32px;
  bottom: 0;
  width: 2px;
  background: ${({ $isLast }) => $isLast ? 'transparent' : '#e8e8e8'};
`

const TimelineIconWrapper = styled.div`
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${({ $bg }) => $bg};
  color: ${({ $color }) => $color};
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;

  svg {
    font-size: 16px;
  }
`

const TimelineContent = styled.div`
  flex: 1;
  margin-left: 16px;
  padding-top: 4px;
`

const TimelineHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`

const TimelineAction = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${({ $color }) => $color};
`

const TimelineDate = styled.span`
  font-size: 12px;
  color: #999;
`

const TimelineDescription = styled.p`
  font-size: 13px;
  color: #595959;
  margin: 6px 0 0 0;
  line-height: 1.5;
`

const TimelineActor = styled.div`
  font-size: 12px;
  color: #8c8c8c;
  margin-top: 4px;

  strong {
    font-weight: 600;
    color: #595959;
  }
`

const StatusChange = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
`

const StatusBadge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  background: ${({ $status }) => taskStatusMeta($status).bg};
  color: ${({ $status }) => taskStatusMeta($status).color};
`

const Arrow = styled.span`
  color: #bfbfbf;
  font-size: 14px;
`

const ExternalEmailTag = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
  padding: 6px 12px;
  background: #e6fffb;
  border: 1px solid #b5f5ec;
  border-radius: 6px;
  font-size: 13px;
  color: #006d75;

  svg {
    color: #13c2c2;
  }

  span {
    font-weight: 600;
  }
`

const RejectionReason = styled.div`
  margin-top: 8px;
  padding: 8px 12px;
  background: #fff1f0;
  border-radius: 6px;
  font-size: 13px;
  color: #cf1322;
  font-style: italic;
`

const TitleChange = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  flex-wrap: wrap;
`

const TitleValue = styled.span`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  background: ${({ $type }) => $type === 'old' ? '#f5f5f5' : '#ecfeff'};
  color: ${({ $type }) => $type === 'old' ? '#8c8c8c' : '#0891b2'};
  ${({ $type }) => $type === 'old' && 'text-decoration: line-through;'}
`

const EmptyState = styled.div`
  padding: 24px;
  text-align: center;
  color: #999;
  font-size: 14px;
`

const GroupToggle = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 8px;
  padding: 4px 10px;
  background: transparent;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  font-size: 12px;
  color: #475569;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s, color 0.15s;

  &:hover {
    background: #f8fafc;
    border-color: #cbd5e1;
    color: #0f172a;
  }
`

const NestedTimeline = styled.div`
  margin-top: 10px;
  padding-left: 6px;
  border-left: 2px solid #e5e7eb;
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const NestedRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
  padding-left: 8px;
  font-size: 12px;
  color: #64748b;
`

const NestedDot = styled.span`
  width: 6px;
  height: 6px;
  margin-top: 6px;
  background: #cbd5e1;
  border-radius: 50%;
  flex-shrink: 0;
`

const NestedTime = styled.span`
  font-variant-numeric: tabular-nums;
  color: #475569;
  font-weight: 500;
`

const NestedDescription = styled.span`
  color: #94a3b8;
  font-style: italic;
`
